import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount, usePrevious } from 'react-use';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import unset from 'lodash/unset';
import compact from 'lodash/compact';
import { useHistory, useRouteMatch } from 'react-router-dom';

import {
  routablePointAdded,
  routablePointRemoved,
  setRoutablePointActive,
  selectRoutablePoints,
} from '../../../store/item-edit';
import Responsive, { useMobile } from '../../responsive/Responsive';
import {
  compilePath,
  FEEDBACK_WIZARD_STEP,
} from '../../../utils/helpers/routes';
import Button, { appearances } from '../../controls/button/Button';
import {
  fieldValidator as routablePointsFieldValidator,
  getNewRoutablePoint,
} from '../../routable-points/RoutablePoints';
import { FIELD_TYPES } from '../field/Field';
import Form from '../form/Form';

import './AddRoutablePointsForm.scss';

export const formValidator = (values) => {
  if (!values?.routablePoints) {
    return;
  }

  const validationErrors = routablePointsFieldValidator(values.routablePoints);

  if (!isEmpty(validationErrors)) {
    return 'Routable points are not valid.';
  }
};

const getFormFields = ({
  autoAddedRoutablePoint,
  activeRoutablePointIndex,
  initialRoutablePointName,
  onAddRoutablePoint,
  onEditRoutablePointLocation,
  onRemoveRoutablePoint,
  poiCategories,
}) => [
  {
    field: 'routablePoints',
    defaultValue: [],
    getPrefillValue: () => [autoAddedRoutablePoint],
    type: FIELD_TYPES.routablePoints,
    errorShownByControl: true,
    validate: routablePointsFieldValidator,
    controlProps: {
      initialPointName: initialRoutablePointName,
      activePointIndex: activeRoutablePointIndex,
      poiCategories,
      onAddRoutablePoint,
      onEditRoutablePointLocation,
      onRemoveRoutablePoint,
    },
    checkChanged: () => true,
    updateErrorAfterChange: (error, path) => {
      if (!error) {
        return error;
      }

      if (path) {
        unset(error, path);
      }

      return compact(error);
    },
  },
];

export interface AddRoutablePointsFormProps {
  baseUrl;
  item;
  poiCategories?: string[];
  formDisabled?;
  formState;
  onFormStateChange;
  onSubmit;
}

interface RouteMatchParams {
  wizardStep;
  parameter;
}

const AddRoutablePointsForm = ({
  baseUrl,
  item,
  poiCategories,
  formDisabled,
  formState,
  onFormStateChange,
  onSubmit,
}: AddRoutablePointsFormProps) => {
  const history = useHistory();
  const match = useRouteMatch<RouteMatchParams>();
  const isMobile = useMobile();

  const dispatch = useDispatch();

  const initialStateRef = useRef(formState);
  const setFormState = useCallback(
    (...args) => onFormStateChange?.(...args),
    [onFormStateChange],
  );

  const mapRoutablePoints = useSelector(selectRoutablePoints);

  // used one time in useMount, no reason to memoize.
  const initializeFirstPoint = () => {
    // not initialized form is a flag to add new point
    if (!isEmpty(formState)) {
      return;
    }

    dispatch(routablePointAdded());

    if (isMobile) {
      history.replace({
        pathname: compilePath('routable-points', baseUrl, {
          wizardStep: FEEDBACK_WIZARD_STEP.location,
          parameter: 0,
        }),
        search: history.location.search,
      });
    }
  };

  useMount(() => {
    initializeFirstPoint();
  });

  const [isFeedbackFormShown, setIsFeedbackFormShown] = useState(!!formState);
  // guarantee that feedback is always filled with all the data on mobile
  useEffect(() => {
    if (!isFeedbackFormShown) {
      if (
        !isMobile ||
        match.params.wizardStep === FEEDBACK_WIZARD_STEP.feedback
      ) {
        setIsFeedbackFormShown(true);
      }
    } else {
      if (isMobile && !match.params.wizardStep) {
        history.replace({
          pathname: compilePath('routable-points', baseUrl, {
            wizardStep: FEEDBACK_WIZARD_STEP.feedback,
          }),
          search: history.location.search,
        });
      }
    }
  }, [isFeedbackFormShown, isMobile, match, history, baseUrl]);

  const handleAddRoutablePoint = useCallback(
    (idx) => {
      dispatch(routablePointAdded());

      if (isMobile) {
        history.push({
          pathname: compilePath('routable-points', baseUrl, {
            wizardStep: FEEDBACK_WIZARD_STEP.location,
            parameter: idx,
          }),
          search: history.location.search,
        });
      }
    },
    [history, dispatch, isMobile, baseUrl],
  );

  const handleEditRoutablePointLocation = useCallback(
    (idx) => {
      history.push({
        pathname: compilePath('routable-points', baseUrl, {
          wizardStep: FEEDBACK_WIZARD_STEP.location,
          parameter: idx,
        }),
        search: history.location.search,
      });
    },
    [history, baseUrl],
  );

  const handleRemoveRoutablePoint = useCallback(
    (idx) => dispatch(routablePointRemoved(idx)),
    [dispatch],
  );

  const activeRoutablePointIndex = useMemo(
    () =>
      mapRoutablePoints
        ? mapRoutablePoints.findIndex((point) => point.active)
        : -1,
    [mapRoutablePoints],
  );

  const activeRoutablePoint = useMemo(
    () => mapRoutablePoints?.[activeRoutablePointIndex] || null,
    [mapRoutablePoints, activeRoutablePointIndex],
  );

  const prevIsMobile = usePrevious(isMobile);
  useEffect(() => {
    if (isMobile) {
      if (match.params.wizardStep !== FEEDBACK_WIZARD_STEP.location) {
        dispatch(setRoutablePointActive());
      }
    } else if (prevIsMobile) {
      dispatch(setRoutablePointActive());
    }
  }, [dispatch, isMobile, prevIsMobile, match.params.wizardStep]);

  useEffect(() => {
    if (match.params.wizardStep === FEEDBACK_WIZARD_STEP.location) {
      let idx;
      try {
        idx = Number.parseInt(match.params.parameter);
      } catch (ex) {
        idx = null;
      }

      const rerouteToFeedback = () =>
        history.replace({
          pathname: compilePath('routable-points', baseUrl, {
            wizardStep: FEEDBACK_WIZARD_STEP.feedback,
          }),
          search: history.location.search,
        });

      if (isNil(idx) || (idx < 0 && idx > mapRoutablePoints.length - 1)) {
        rerouteToFeedback();
        return;
      }

      if (idx !== activeRoutablePointIndex) {
        dispatch(setRoutablePointActive(idx));
      }
    }
  }, [
    dispatch,
    history,
    mapRoutablePoints,
    match,
    baseUrl,
    activeRoutablePointIndex,
  ]);

  const formFields = useMemo(
    () =>
      getFormFields({
        autoAddedRoutablePoint: getNewRoutablePoint(
          item.properties.feature_name,
        ),
        activeRoutablePointIndex: isMobile ? -1 : activeRoutablePointIndex,
        initialRoutablePointName: item.properties.feature_name,
        poiCategories: poiCategories,
        onAddRoutablePoint: handleAddRoutablePoint,
        onEditRoutablePointLocation: handleEditRoutablePointLocation,
        onRemoveRoutablePoint: handleRemoveRoutablePoint,
      }),
    [
      item,
      isMobile,
      activeRoutablePointIndex,
      poiCategories,
      handleAddRoutablePoint,
      handleEditRoutablePointLocation,
      handleRemoveRoutablePoint,
    ],
  );

  const initialValues = useMemo(
    () =>
      initialStateRef.current
        ? initialStateRef.current.values
        : formFields.reduce((acc, { field, defaultValue }) => {
            acc[field] = defaultValue;
            return acc;
          }, {}),
    [formFields],
  );

  useEffect(() => {
    // initial form state may be null, undefined or an empty object
    if (isEmpty(formState) && !!mapRoutablePoints?.length) {
      const values = formFields.reduce(
        (acc, { field, defaultValue, getPrefillValue }) => {
          acc[field] = getPrefillValue?.() || defaultValue;
          return acc;
        },
        {},
      );

      setFormState({ values });
    }
  }, [formState, setFormState, formFields, mapRoutablePoints]);

  const handleLocationFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath('routable-points', baseUrl, {
          wizardStep: FEEDBACK_WIZARD_STEP.feedback,
        }),
        search: history.location.search,
      }),
    [history, baseUrl],
  );

  const handleSubmit = useCallback(
    (...args) => {
      onFormStateChange?.(formState);
      onSubmit?.(...args);
    },
    [onFormStateChange, onSubmit, formState],
  );

  const { wizardStep } = match.params;

  if (!formState) {
    return null;
  }

  return (
    <>
      <Responsive mobile={false}>
        <Form
          className="add-routable-points-form"
          state={formState}
          fields={formFields}
          initialValues={initialValues}
          formDisabled={formDisabled}
          onStateChange={setFormState}
          submitLabel="Save"
          onSubmit={handleSubmit}
        />
      </Responsive>
      <Responsive mobile={true}>
        {wizardStep === FEEDBACK_WIZARD_STEP.location && (
          <div className="add-routable-points-form__location-pickup-button-container">
            <Button
              appearance={appearances.PRIMARY}
              disabled={
                formDisabled ||
                (activeRoutablePoint && !activeRoutablePoint.moved)
              }
              onClick={handleLocationFormSubmit}
            >
              Next
            </Button>
          </div>
        )}
        {wizardStep === FEEDBACK_WIZARD_STEP.feedback && (
          <Form
            className="add-routable-points-form"
            state={formState}
            fields={formFields}
            initialValues={initialValues}
            formDisabled={formDisabled}
            stepLabel="1/2"
            onStateChange={setFormState}
            submitLabel="Save"
            onSubmit={handleSubmit}
          />
        )}
      </Responsive>
    </>
  );
};

export default AddRoutablePointsForm;
